import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const useCandidateSearch = (firebaseUser) => {
    const location = useLocation();

    const [jobDescription, setJobDescription] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [infoMessage, setInfoMessage] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'final_score', direction: 'desc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    //const rowsPerPageOptions = [10, 25, 50, 100]; future use

    useEffect(() => {
        const initialDescFromLocation = location.state?.jobDescription;
        if (initialDescFromLocation) {
            setJobDescription(initialDescFromLocation);
        }
    }, [location.state]);

    const handleJobDescriptionChange = useCallback((newDescription) => {
        setJobDescription(newDescription);
        if (error) setError(null);
        if (infoMessage) setInfoMessage(null);
    }, [error, infoMessage]);

    const handleSortRequest = useCallback((property) => {
        const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
        setPage(0);
    }, [sortConfig]);

    const sortedCandidates = useMemo(() => {
        let sortableItems = [...candidates];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key] === null || a[sortConfig.key] === undefined ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : a[sortConfig.key];
                const valB = b[sortConfig.key] === null || b[sortConfig.key] === undefined ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : b[sortConfig.key];
                if (sortConfig.key === 'name') {
                    const nameA = String(valA).toLowerCase();
                    const nameB = String(valB).toLowerCase();
                    if (nameA < nameB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (nameA > nameB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [candidates, sortConfig]);

    const handleSubmitSearch = useCallback(async (currentSearchDescriptionFromView) => {
        const searchDesc = currentSearchDescriptionFromView || jobDescription; // Use view's current text or hook's state

        if (!searchDesc || searchDesc.trim() === '') {
            setError("Please enter a job description to start your search.");
            return;
        }
        if (!firebaseUser) {
            setError("User not authenticated. Please sign in to search for candidates.");
            setIsProcessing(false);
            return;
        }

        setIsProcessing(true);
        setError(null);
        setInfoMessage(null);
        setCandidates([]);
        setPage(0);
        setSortConfig({ key: 'final_score', direction: 'desc' });

        try {
            const idToken = await firebaseUser.getIdToken(true);

            const apiUrl = "https://candidate-matcher-qa-129582643714.us-central1.run.app/api/match-candidates";

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
                body: JSON.stringify({ job_description: searchDesc })
            });

            if (!response.ok) {
                let errorData;
                try { errorData = await response.json(); } catch (e) { errorData = await response.text(); }
                console.error("API Error Response:", errorData);
                throw new Error(`API request failed with status ${response.status}. ${errorData?.message || errorData || 'Unknown API error'}`);
            }

            const resultData = await response.json();
            const rawCandidates = resultData.candidates || [];
            setCandidates(rawCandidates);

            if (!rawCandidates || rawCandidates.length === 0) {
                setInfoMessage("No candidates found matching your description. Try refining your search terms.");
            } else {
                setError(null);
            }
        } catch (err) {
            console.error("Error submitting search or getting token:", err);
            if (err instanceof TypeError && err.message.includes("getIdToken is not a function")) {
                setError("Authentication error: Session issue. Please sign out and sign in again.");
            } else { setError(err.message || "An unexpected error occurred."); }
            setCandidates([]);
        } finally {
            setIsProcessing(false);
        }
    }, [firebaseUser, jobDescription]); // jobDescription from hook state is a dependency here

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const paginatedCandidates = useMemo(() => {
        return sortedCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedCandidates, page, rowsPerPage]);

    return {
        jobDescription, // Current job description in the text field
        handleJobDescriptionChange, // To update jobDescription
        handleSubmitSearch, // The main search submission function
        sortedCandidates, // All candidates after sorting (for total count)
        paginatedCandidates, // Candidates for the current page
        isProcessing,
        error,
        infoMessage,
        sortConfig,
        handleSortRequest,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        rowsPerPageOptions: [10, 25, 50, 100] // Provide options directly from hook
    };
};
